import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import { LogIn } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Log in — PixelEmpire AI",
    description: "Sign in to your PixelEmpire AI account to manage your permanent advertising blocks.",
    path: "/login",
    noindex: true,
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate(next);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="hero-orb bg-indigo-500" style={{ width: 500, height: 500, top: -120, left: -100 }} />
      <main className="pt-40 pb-24 px-4 max-w-md mx-auto">
        <div className="glass-strong p-8" data-testid="login-form">
          <div className="pill"><span>Welcome back</span></div>
          <h1 className="mt-3 font-display text-4xl">Sign in</h1>
          <p className="mt-2 text-sm text-[var(--fg-muted)]">
            Manage your pixel blocks, upload logos, and view analytics.
          </p>

          <div className="mt-6">
            <GoogleLoginButton testid="login-google" />
          </div>
          {params.get("google_error") && (
            <div className="mt-3 text-xs text-red-400" data-testid="google-oauth-error">
              Google sign-in failed: {params.get("google_error")}. Try again or use email + password.
            </div>
          )}

          <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-widest text-[var(--fg-muted)]">
            <span className="flex-1 h-px bg-white/10" /> or <span className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={onSubmit} className="mt-4 space-y-3">
            <input
              data-testid="login-email"
              type="email" placeholder="Email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="input-lux"
            />
            <input
              data-testid="login-password"
              type="password" placeholder="Password" required minLength={6}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="input-lux"
            />
            {error && <div className="text-sm text-red-500" data-testid="login-error">{error}</div>}
            <button data-testid="login-submit" className="btn-primary w-full" disabled={busy}>
              <LogIn className="w-4 h-4" />{busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-6 text-sm text-center text-[var(--fg-muted)]">
            No account? <Link to="/register" className="underline">Create one</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
